"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { HiOutlineMail } from "react-icons/hi";
import { EmailThreadView } from "@/components/email-thread-view";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { useUser } from "@/contexts/user-provider";
import { formatDate } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { markEmailThreadAsRead } from "@/api/provider/comms/email/email";
import { emailQueryOptions } from "@/api/provider/comms/email/email-query";
interface EmailThreadDialogProps {
  threadId?: string;
  selectedMessageId?: string;
  chatId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}
function formatLastUpdated(dateString: string | null): string {
  if (!dateString) return "";
  return formatDate(dateString);
}
export function EmailThreadDialog(t0) {
  const $ = _c(39);
  const {
    threadId,
    selectedMessageId,
    chatId,
    isOpen,
    onOpenChange
  } = t0;
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasPermission(currentUser, PERMISSIONS.MESSAGING_EMAILS_WRITE);
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canWrite = t1;
  let t2;
  if ($[2] !== threadId) {
    t2 = emailQueryOptions.thread(threadId || "", "clinic");
    $[2] = threadId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isOpen && !!threadId;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: thread,
    isLoading
  } = useQuery(t4);
  let t5;
  let t6;
  if ($[7] !== isOpen || $[8] !== thread || $[9] !== threadId) {
    t5 = () => {
      if (isOpen && threadId && thread && !thread.isRead) {
        markEmailThreadAsRead(threadId).catch(_temp);
      }
    };
    t6 = [isOpen, threadId, thread];
    $[7] = isOpen;
    $[8] = thread;
    $[9] = threadId;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  const messageCount = thread?.messages?.length ?? 0;
  const lastMessageDate = thread?.lastMessageDate;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="shrink-0 p-2 rounded-lg bg-background-secondary"><HiOutlineMail className="h-5 w-5 text-foreground-secondary" /></div>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== isLoading || $[14] !== thread?.subject) {
    t8 = isLoading ? <span className="h-6 w-3/4 rounded bg-muted animate-pulse inline-block" /> : thread?.subject || "No Subject";
    $[13] = isLoading;
    $[14] = thread?.subject;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <DrawerTitle className="line-clamp-2">{t8}</DrawerTitle>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== isLoading || $[19] !== lastMessageDate || $[20] !== messageCount) {
    t10 = <DrawerDescription className="mt-1">{isLoading ? <span className="h-4 w-1/3 rounded bg-muted animate-pulse inline-block" /> : <>{messageCount} {messageCount === 1 ? "message" : "messages"}{lastMessageDate && <>{" "}· Last updated{" "}{formatLastUpdated(lastMessageDate)}</>}</>}</DrawerDescription>;
    $[18] = isLoading;
    $[19] = lastMessageDate;
    $[20] = messageCount;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <DrawerHeader className="px-4 md:px-0"><div className="flex items-start gap-3">{t7}<div className="flex-1 min-w-0">{t9}{t10}</div></div></DrawerHeader>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = isOpen ? threadId : "closed";
  let t13;
  if ($[25] !== canWrite || $[26] !== chatId || $[27] !== isLoading || $[28] !== selectedMessageId || $[29] !== t12 || $[30] !== thread) {
    t13 = <DrawerBody><EmailThreadView thread={thread} isLoading={isLoading} selectedMessageId={selectedMessageId} chatId={chatId} showHeader={false} enableMessageActions={canWrite} threadScope="clinic" resetKey={t12} bodyClassName="px-0 py-1" /></DrawerBody>;
    $[25] = canWrite;
    $[26] = chatId;
    $[27] = isLoading;
    $[28] = selectedMessageId;
    $[29] = t12;
    $[30] = thread;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t13) {
    t14 = <DrawerContent className="md:max-w-4xl w-full px-0 md:px-4 bg-background backdrop-blur-none">{t11}{t13}</DrawerContent>;
    $[32] = t11;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== isOpen || $[36] !== onOpenChange || $[37] !== t14) {
    t15 = <Drawer open={isOpen} onOpenChange={onOpenChange}>{t14}</Drawer>;
    $[35] = isOpen;
    $[36] = onOpenChange;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
}
function _temp() {}